import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChatSidebar, Chat } from './ChatSidebar';
import {
  getSessions,
  createSession,
  updateSession,
  deleteSession,
} from './api';
import { EditChatModal } from './EditChatModal';
import { useToastHook } from '../appToast';
import { getOrgId } from '@devd-client/api';

export const ChatSidebarWrapper = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const sessionId = pathname.match(/\/dyna-ai\/([^/]+)/)?.[1];
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);

  const [newToast] = useToastHook();

  const fetchSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const sessions = await getSessions();
      setChats(
        sessions.map((s) => ({
          id: s.id,
          title: s.title || 'New Chat',
          updatedAt: s.updated_at || new Date().toISOString(),
        }))
      );
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load sessions only on initial mount
  useEffect(() => {
    fetchSessions();
  }, []);

  // Listen for new session creation events and add directly to state
  useEffect(() => {
    const handleSessionCreated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newSession = customEvent.detail;
      if (newSession) {
        setChats((prev) => {
          if (prev.some((chat) => chat.id === newSession.id)) {
            return prev;
          }
          return [
            {
              id: newSession.id,
              title: newSession.title || 'New Chat',
              updatedAt: newSession.created_at || new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }
    };

    window.addEventListener('chat-session-created', handleSessionCreated);
    return () => {
      window.removeEventListener('chat-session-created', handleSessionCreated);
    };
  }, []);

  // Listen for session rename events from streaming API and update sidebar title
  useEffect(() => {
    const handleSessionRenamed = (event: Event) => {
      const { id, title } = (event as CustomEvent).detail || {};
      if (!id || !title) return;

      setChats((prev) =>
        prev.map((chat) =>
          chat.id === id
            ? {
                ...chat,
                title,
                updatedAt: new Date().toISOString(),
              }
            : chat
        )
      );
    };

    window.addEventListener('chat-session-renamed', handleSessionRenamed);
    return () => {
      window.removeEventListener('chat-session-renamed', handleSessionRenamed);
    };
  }, []);

  const handleNewChat = useCallback(async () => {
    try {
      setIsCreatingNewChat(true);
      const newSession = await createSession('New Chat');

      setChats((prev) => [
        {
          id: newSession.id,
          title: newSession.title || 'New Chat',
          updatedAt: newSession.created_at || new Date().toISOString(),
        },
        ...prev,
      ]);

      try {
        if (window.analytics) {
          window.analytics.track('ai_chat_session_created', {
            orgId: getOrgId(),
            properties: {
              sessionId: newSession.id,
              sessionTitle: newSession.title ?? 'New Chat',
            },
          });
        }
      } catch (analyticsError) {
        console.warn('Analytics tracking failed:', analyticsError);
      }

      navigate(`/dyna-ai/${newSession.id}`);
    } catch (error) {
      console.error('Error creating session:', error);
    } finally {
      setIsCreatingNewChat(false);
    }
  }, [navigate]);

  const handleChatSelect = useCallback(
    (chatId: string) => {
      navigate(`/dyna-ai/${chatId}`);
    },
    [navigate]
  );

  const handleEditChat = useCallback((chatId: string) => {
    setEditingChatId(chatId);
  }, []);

  const handleSaveChatTitle = useCallback(
    async (chatId: string, title: string) => {
      setIsUpdatingTitle(true);
      try {
        const updatedSession = await updateSession(chatId, title);

        setChats((prev) =>
          prev.map((c) =>
            c.id === chatId
              ? {
                  ...c,
                  title: updatedSession.title,
                  updatedAt: updatedSession.updated_at,
                }
              : c
          )
        );

        newToast({
          message: 'Chat title updated successfully',
          status: 'success',
        });
      } catch (error) {
        console.error('Error updating chat title:', error);
        newToast({
          message: 'Error updating chat title',
          status: 'error',
        });
        throw error;
      } finally {
        setIsUpdatingTitle(false);
      }
    },
    [newToast]
  );

  const handleDeleteChat = useCallback(
    async (chatId: string) => {
      setDeletingChatId(chatId);

      const isCurrentSession = sessionId === chatId;
      if (isCurrentSession) {
        navigate('/dyna-ai', { replace: true });
      }

      try {
        await deleteSession(chatId);

        const remaining = chats.filter((chat) => chat.id !== chatId);

        setChats(remaining);
      } catch (error) {
        console.error('Error deleting session:', error);
      } finally {
        setDeletingChatId(null);
      }
    },
    [sessionId, navigate, chats]
  );

  const editingChat = editingChatId
    ? chats.find((c) => c.id === editingChatId)
    : null;

  return (
    <>
      <ChatSidebar
        chats={chats}
        selectedChatId={sessionId}
        onChatSelect={handleChatSelect}
        onNewChat={handleNewChat}
        onEditChat={handleEditChat}
        onDeleteChat={handleDeleteChat}
        isLoading={isLoading}
        deletingChatId={deletingChatId}
        isCreatingNewChat={isCreatingNewChat}
      />
      {editingChatId && (
        <EditChatModal
          isOpen={!!editingChatId}
          onClose={() => setEditingChatId(null)}
          chatId={editingChatId || undefined}
          currentTitle={editingChat?.title}
          onSave={handleSaveChatTitle}
          isLoading={isUpdatingTitle}
        />
      )}
    </>
  );
};

export default ChatSidebarWrapper;
